import Vue from 'vue'
import Vuex from 'vuex'
import Meta from 'vue-meta'
import ClientOnly from 'vue-client-only'
import NoSsr from 'vue-no-ssr'
import { createRouter } from './router.js'
import NuxtChild from './components/nuxt-child.js'
import NuxtError from '../layouts/error.vue'
import Nuxt from './components/nuxt.js'
import App from './App.js'
import { setContext, getLocation, getRouteData, normalizeError } from './utils'
import { createStore } from './store.js'

/* Plugins */

import nuxt_plugin_plugin_08283616 from 'nuxt_plugin_plugin_08283616' // Source: ./components/plugin.js (mode: 'all')
import nuxt_plugin_markdownit_f3de385c from 'nuxt_plugin_markdownit_f3de385c' // Source: ./markdownit.js (mode: 'all')
import nuxt_plugin_googlemaps_1f3cd497 from 'nuxt_plugin_googlemaps_1f3cd497' // Source: ./google-maps.js (mode: 'all')
import nuxt_plugin_gtm_2a91b402 from 'nuxt_plugin_gtm_2a91b402' // Source: ./gtm.js (mode: 'all')
import nuxt_plugin_pluginutils_1e7d398f from 'nuxt_plugin_pluginutils_1e7d398f' // Source: ./nuxt-i18n/plugin.utils.js (mode: 'all')
import nuxt_plugin_pluginrouting_2a159f1a from 'nuxt_plugin_pluginrouting_2a159f1a' // Source: ./nuxt-i18n/plugin.routing.js (mode: 'all')
import nuxt_plugin_pluginmain_21e99ad6 from 'nuxt_plugin_pluginmain_21e99ad6' // Source: ./nuxt-i18n/plugin.main.js (mode: 'all')
import nuxt_plugin_axios_2e0a522c from 'nuxt_plugin_axios_2e0a522c' // Source: ./axios.js (mode: 'all')
import nuxt_plugin_vueamplitudeplugin_201672f0 from 'nuxt_plugin_vueamplitudeplugin_201672f0' // Source: ./vue-amplitude-plugin.js (mode: 'client')
import nuxt_plugin_redirect_29812e27 from 'nuxt_plugin_redirect_29812e27' // Source: ../plugins/redirect.js (mode: 'all')
import nuxt_plugin_directioncontrol_17ba8308 from 'nuxt_plugin_directioncontrol_17ba8308' // Source: ../plugins/direction-control.js (mode: 'all')
import nuxt_plugin_vuea2b_81e4763e from 'nuxt_plugin_vuea2b_81e4763e' // Source: ../plugins/vue-a2b.js (mode: 'all')
import nuxt_plugin_vuejsmodal_a7853c1a from 'nuxt_plugin_vuejsmodal_a7853c1a' // Source: ../plugins/vue-js-modal.js (mode: 'all')
import nuxt_plugin_vuecountryflag_0ce399ce from 'nuxt_plugin_vuecountryflag_0ce399ce' // Source: ../plugins/vue-country-flag.js (mode: 'all')
import nuxt_plugin_jscookie_429414fa from 'nuxt_plugin_jscookie_429414fa' // Source: ../plugins/js-cookie.js (mode: 'all')
import nuxt_plugin_vuestripe_0b8fed8f from 'nuxt_plugin_vuestripe_0b8fed8f' // Source: ../plugins/vue-stripe.js (mode: 'client')
import nuxt_plugin_lead_60aafca7 from 'nuxt_plugin_lead_60aafca7' // Source: ../plugins/lead.js (mode: 'client')
import nuxt_plugin_vuevuelidate_f56cd294 from 'nuxt_plugin_vuevuelidate_f56cd294' // Source: ../plugins/vue-vuelidate (mode: 'all')
import nuxt_plugin_vcalendar_55bbb5ae from 'nuxt_plugin_vcalendar_55bbb5ae' // Source: ../plugins/v-calendar.js (mode: 'client')
import nuxt_plugin_vuenotificationsssr_43ce5c9c from 'nuxt_plugin_vuenotificationsssr_43ce5c9c' // Source: ../plugins/vue-notifications-ssr (mode: 'all')
import nuxt_plugin_vuenotificationsclient_e2f087aa from 'nuxt_plugin_vuenotificationsclient_e2f087aa' // Source: ../plugins/vue-notifications-client (mode: 'client')

// Component: <ClientOnly>
Vue.component(ClientOnly.name, ClientOnly)

// TODO: Remove in Nuxt 3: <NoSsr>
Vue.component(NoSsr.name, {
  ...NoSsr,
  render (h, ctx) {
    if (process.client && !NoSsr._warned) {
      NoSsr._warned = true

      console.warn('<no-ssr> has been deprecated and will be removed in Nuxt 3, please use <client-only> instead')
    }
    return NoSsr.render(h, ctx)
  }
})

// Component: <NuxtChild>
Vue.component(NuxtChild.name, NuxtChild)
Vue.component('NChild', NuxtChild)

// Component NuxtLink is imported in server.js or client.js

// Component: <Nuxt>
Vue.component(Nuxt.name, Nuxt)

Object.defineProperty(Vue.prototype, '$nuxt', {
  get() {
    const globalNuxt = this.$root ? this.$root.$options.$nuxt : null
    if (process.client && !globalNuxt && typeof window !== 'undefined') {
      return window.$nuxt
    }
    return globalNuxt
  },
  configurable: true
})

Vue.use(Meta, {"keyName":"head","attribute":"data-n-head","ssrAttribute":"data-n-head-ssr","tagIDKeyName":"hid"})

const defaultTransition = {"name":"page","mode":"out-in","appear":false,"appearClass":"appear","appearActiveClass":"appear-active","appearToClass":"appear-to"}

const originalRegisterModule = Vuex.Store.prototype.registerModule

function registerModule (path, rawModule, options = {}) {
  const preserveState = process.client && (
    Array.isArray(path)
      ? !!path.reduce((namespacedState, path) => namespacedState && namespacedState[path], this.state)
      : path in this.state
  )
  return originalRegisterModule.call(this, path, rawModule, { preserveState, ...options })
}

async function createApp(ssrContext, config = {}) {
  const store = createStore(ssrContext)
  const router = await createRouter(ssrContext, config, { store })

  // Add this.$router into store actions/mutations
  store.$router = router

  // Fix SSR caveat https://github.com/nuxt/nuxt.js/issues/3757#issuecomment-414689141
  store.registerModule = registerModule

  // Create Root instance

  // here we inject the router and store to all child components,
  // making them available everywhere as `this.$router` and `this.$store`.
  const app = {
    head: {"script":[{"src":"https:\u002F\u002Fjs.stripe.com\u002Fv3"},{"src":"https:\u002F\u002Fassets.calendly.com\u002Fassets\u002Fexternal\u002Fwidget.js","async":true,"defer":true},{"hid":"gtm-script","innerHTML":"if(!window._gtm_init){window._gtm_init=1;(function(w,n,d,m,e,p){w[d]=(w[d]==1||n[d]=='yes'||n[d]==1||n[m]==1||(w[e]&&w[e][p]&&w[e][p]()))?1:0})(window,navigator,'doNotTrack','msDoNotTrack','external','msTrackingProtectionEnabled');(function(w,d,s,l,x,y){w[x]={};w._gtm_inject=function(i){if(w.doNotTrack||w[x][i])return;w[x][i]=1;w[l]=w[l]||[];w[l].push({'gtm.start':new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],j=d.createElement(s);j.async=true;j.src='https:\u002F\u002Fwww.googletagmanager.com\u002Fgtm.js?id='+i;f.parentNode.insertBefore(j,f);};w[y]('GTM-MTN6DPZ')})(window,document,'script','dataLayer','_gtm_ids','_gtm_inject')}"}],"title":"Thrive State","htmlAttrs":{"lang":"en"},"meta":[{"charset":"utf-8"},{"name":"viewport","content":"width=device-width, initial-scale=1, maximum-scale=1.0, user-scalable=no"},{"hid":"title","name":"title","content":"Thrive State"},{"hid":"og:title","name":"og:title","content":"Thrive State"},{"hid":"description","name":"description","content":"Explore The Real Estate Market In Dubai And Find The Best Deals"},{"hid":"og:description","property":"og:description","content":"Explore The Real Estate Market In Dubai And Find The Best Deals "},{"hid":"og:type","property":"og:type","content":"website"},{"hid":"og:image","property":"og:image","content":"\u002Flogo\u002Flogo.svg"},{"hid":"og:url","property":"og:url","content":"https:\u002F\u002Fthrivestate.io"},{"name":"format-detection","content":"telephone=no"}],"link":[{"rel":"icon","type":"image\u002Fsvg+xml","href":"\u002Flogo\u002FlogoDark.svg"},{"rel":"icon","href":"\u002Flogo\u002FlogoDark.png","sizes":"any"}],"style":[],"noscript":[{"hid":"gtm-noscript","pbody":true,"innerHTML":"\u003Ciframe src=\"https:\u002F\u002Fwww.googletagmanager.com\u002Fns.html?id=GTM-MTN6DPZ&\" height=\"0\" width=\"0\" style=\"display:none;visibility:hidden\" title=\"gtm\"\u003E\u003C\u002Fiframe\u003E"}],"__dangerouslyDisableSanitizersByTagID":{"gtm-script":["innerHTML"],"gtm-noscript":["innerHTML"]}},

    store,
    router,
    nuxt: {
      defaultTransition,
      transitions: [defaultTransition],
      setTransitions (transitions) {
        if (!Array.isArray(transitions)) {
          transitions = [transitions]
        }
        transitions = transitions.map((transition) => {
          if (!transition) {
            transition = defaultTransition
          } else if (typeof transition === 'string') {
            transition = Object.assign({}, defaultTransition, { name: transition })
          } else {
            transition = Object.assign({}, defaultTransition, transition)
          }
          return transition
        })
        this.$options.nuxt.transitions = transitions
        return transitions
      },

      err: null,
      dateErr: null,
      error (err) {
        err = err || null
        app.context._errored = Boolean(err)
        err = err ? normalizeError(err) : null
        let nuxt = app.nuxt // to work with @vue/composition-api, see https://github.com/nuxt/nuxt.js/issues/6517#issuecomment-573280207
        if (this) {
          nuxt = this.nuxt || this.$options.nuxt
        }
        nuxt.dateErr = Date.now()
        nuxt.err = err
        // Used in src/server.js
        if (ssrContext) {
          ssrContext.nuxt.error = err
        }
        return err
      }
    },
    ...App
  }

  // Make app available into store via this.app
  store.app = app

  const next = ssrContext ? ssrContext.next : location => app.router.push(location)
  // Resolve route
  let route
  if (ssrContext) {
    route = router.resolve(ssrContext.url).route
  } else {
    const path = getLocation(router.options.base, router.options.mode)
    route = router.resolve(path).route
  }

  // Set context to app.context
  await setContext(app, {
    store,
    route,
    next,
    error: app.nuxt.error.bind(app),
    payload: ssrContext ? ssrContext.payload : undefined,
    req: ssrContext ? ssrContext.req : undefined,
    res: ssrContext ? ssrContext.res : undefined,
    beforeRenderFns: ssrContext ? ssrContext.beforeRenderFns : undefined,
    beforeSerializeFns: ssrContext ? ssrContext.beforeSerializeFns : undefined,
    ssrContext
  })

  function inject(key, value) {
    if (!key) {
      throw new Error('inject(key, value) has no key provided')
    }
    if (value === undefined) {
      throw new Error(`inject('${key}', value) has no value provided`)
    }

    key = '$' + key
    // Add into app
    app[key] = value
    // Add into context
    if (!app.context[key]) {
      app.context[key] = value
    }

    // Add into store
    store[key] = app[key]

    // Check if plugin not already installed
    const installKey = '__nuxt_' + key + '_installed__'
    if (Vue[installKey]) {
      return
    }
    Vue[installKey] = true
    // Call Vue.use() to install the plugin into vm
    Vue.use(() => {
      if (!Object.prototype.hasOwnProperty.call(Vue.prototype, key)) {
        Object.defineProperty(Vue.prototype, key, {
          get () {
            return this.$root.$options[key]
          }
        })
      }
    })
  }

  // Inject runtime config as $config
  inject('config', config)

  if (process.client) {
    // Replace store state before plugins execution
    if (window.__NUXT__ && window.__NUXT__.state) {
      store.replaceState(window.__NUXT__.state)
    }
  }

  // Add enablePreview(previewData = {}) in context for plugins
  if (process.static && process.client) {
    app.context.enablePreview = function (previewData = {}) {
      app.previewData = Object.assign({}, previewData)
      inject('preview', previewData)
    }
  }
  // Plugin execution

  if (typeof nuxt_plugin_plugin_08283616 === 'function') {
    await nuxt_plugin_plugin_08283616(app.context, inject)
  }

  if (typeof nuxt_plugin_markdownit_f3de385c === 'function') {
    await nuxt_plugin_markdownit_f3de385c(app.context, inject)
  }

  if (typeof nuxt_plugin_googlemaps_1f3cd497 === 'function') {
    await nuxt_plugin_googlemaps_1f3cd497(app.context, inject)
  }

  if (typeof nuxt_plugin_gtm_2a91b402 === 'function') {
    await nuxt_plugin_gtm_2a91b402(app.context, inject)
  }

  if (typeof nuxt_plugin_pluginutils_1e7d398f === 'function') {
    await nuxt_plugin_pluginutils_1e7d398f(app.context, inject)
  }

  if (typeof nuxt_plugin_pluginrouting_2a159f1a === 'function') {
    await nuxt_plugin_pluginrouting_2a159f1a(app.context, inject)
  }

  if (typeof nuxt_plugin_pluginmain_21e99ad6 === 'function') {
    await nuxt_plugin_pluginmain_21e99ad6(app.context, inject)
  }

  if (typeof nuxt_plugin_axios_2e0a522c === 'function') {
    await nuxt_plugin_axios_2e0a522c(app.context, inject)
  }

  if (process.client && typeof nuxt_plugin_vueamplitudeplugin_201672f0 === 'function') {
    await nuxt_plugin_vueamplitudeplugin_201672f0(app.context, inject)
  }

  if (typeof nuxt_plugin_redirect_29812e27 === 'function') {
    await nuxt_plugin_redirect_29812e27(app.context, inject)
  }

  if (typeof nuxt_plugin_directioncontrol_17ba8308 === 'function') {
    await nuxt_plugin_directioncontrol_17ba8308(app.context, inject)
  }

  if (typeof nuxt_plugin_vuea2b_81e4763e === 'function') {
    await nuxt_plugin_vuea2b_81e4763e(app.context, inject)
  }

  if (typeof nuxt_plugin_vuejsmodal_a7853c1a === 'function') {
    await nuxt_plugin_vuejsmodal_a7853c1a(app.context, inject)
  }

  if (typeof nuxt_plugin_vuecountryflag_0ce399ce === 'function') {
    await nuxt_plugin_vuecountryflag_0ce399ce(app.context, inject)
  }

  if (typeof nuxt_plugin_jscookie_429414fa === 'function') {
    await nuxt_plugin_jscookie_429414fa(app.context, inject)
  }

  if (process.client && typeof nuxt_plugin_vuestripe_0b8fed8f === 'function') {
    await nuxt_plugin_vuestripe_0b8fed8f(app.context, inject)
  }

  if (process.client && typeof nuxt_plugin_lead_60aafca7 === 'function') {
    await nuxt_plugin_lead_60aafca7(app.context, inject)
  }

  if (typeof nuxt_plugin_vuevuelidate_f56cd294 === 'function') {
    await nuxt_plugin_vuevuelidate_f56cd294(app.context, inject)
  }

  if (process.client && typeof nuxt_plugin_vcalendar_55bbb5ae === 'function') {
    await nuxt_plugin_vcalendar_55bbb5ae(app.context, inject)
  }

  if (typeof nuxt_plugin_vuenotificationsssr_43ce5c9c === 'function') {
    await nuxt_plugin_vuenotificationsssr_43ce5c9c(app.context, inject)
  }

  if (process.client && typeof nuxt_plugin_vuenotificationsclient_e2f087aa === 'function') {
    await nuxt_plugin_vuenotificationsclient_e2f087aa(app.context, inject)
  }

  // Lock enablePreview in context
  if (process.static && process.client) {
    app.context.enablePreview = function () {
      console.warn('You cannot call enablePreview() outside a plugin.')
    }
  }

  // Wait for async component to be resolved first
  await new Promise((resolve, reject) => {
    // Ignore 404s rather than blindly replacing URL in browser
    if (process.client) {
      const { route } = router.resolve(app.context.route.fullPath)
      if (!route.matched.length) {
        return resolve()
      }
    }
    router.replace(app.context.route.fullPath, resolve, (err) => {
      // https://github.com/vuejs/vue-router/blob/v3.4.3/src/util/errors.js
      if (!err._isRouter) return reject(err)
      if (err.type !== 2 /* NavigationFailureType.redirected */) return resolve()

      // navigated to a different route in router guard
      const unregister = router.afterEach(async (to, from) => {
        if (process.server && ssrContext && ssrContext.url) {
          ssrContext.url = to.fullPath
        }
        app.context.route = await getRouteData(to)
        app.context.params = to.params || {}
        app.context.query = to.query || {}
        unregister()
        resolve()
      })
    })
  })

  return {
    store,
    app,
    router
  }
}

export { createApp, NuxtError }
